import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Music, Package, Heart, LogOut, Coins } from 'lucide-react';
import PurchasedTrackCard from '../components/PurchasedTrackCard';
import { useCredits } from '../contexts/CreditsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { credits, lowThreshold, openBuyModal, costTrack, costBookAudio } = useCredits();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('purchased');
  const [syncing, setSyncing] = useState(false);

  const handleSyncPurchases = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(
        `${API}/api/payments/sync-my-purchases`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      alert(data.message || 'Sync complete');
      // Reload profile to show new tracks/orders
      window.location.reload();
    } catch (e) {
      alert('Sync failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/api/users/profile`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        setProfile(data);
      } catch (e) {
        console.error('Failed to load profile', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="account-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Welcome back</p>
            <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">{user.name}</h1>
            <p className="text-[#A3A3A3] mt-2">{user.email}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleSyncPurchases}
              disabled={syncing}
              className="flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"
              data-testid="sync-purchases-btn"
              title="Refresh your library if a recent purchase isn't showing up"
            >
              {syncing ? 'Syncing...' : 'Sync Purchases'}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 border border-white/20 text-[#A3A3A3] hover:border-red-500 hover:text-red-500 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Streaming Credits */}
        <div
          className={`rounded-lg p-5 md:p-6 mb-8 border ${
            credits <= lowThreshold
              ? 'border-yellow-500/40 bg-yellow-500/5'
              : 'border-[#D4AF37]/30 bg-[#1A1A1A]'
          }`}
          data-testid="account-credits-panel"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
                <Coins size={22} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">Streaming Credits</p>
                <p className="font-['Bebas_Neue'] text-4xl text-white leading-none mt-1" data-testid="account-credit-balance">
                  {credits}
                </p>
                <p className="text-xs text-[#A3A3A3] mt-2">
                  Track stream {costTrack} · Audiobook listen {costBookAudio} · Never expires · Owned tracks always free
                </p>
              </div>
            </div>
            <button
              onClick={() => openBuyModal()}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium text-sm uppercase tracking-wider hover:bg-[#E5C048] transition-colors whitespace-nowrap"
              data-testid="account-buy-credits-btn"
            >
              {credits <= lowThreshold ? 'Top Up Credits' : 'Buy More Credits'}
            </button>
          </div>
          {credits <= lowThreshold && credits > 0 && (
            <p className="text-xs text-yellow-300 mt-4">
              ⚠️ You're running low — about {Math.floor(credits / costTrack)} more track streams before you'll need to refill.
            </p>
          )}
          {credits === 0 && (
            <p className="text-xs text-yellow-300 mt-4">
              You're out of streaming credits. Buy a pack to keep listening, or play any track you've already purchased — those stream free.
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-white/10 mb-8">
          <button
            onClick={() => setActiveTab('purchased')}
            className={`flex items-center gap-2 pb-4 text-sm uppercase tracking-wider transition-colors ${
              activeTab === 'purchased' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A3A3A3] hover:text-white'
            }`}
            data-testid="tab-purchased"
          >
            <Music size={18} />
            Purchased Music
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 pb-4 text-sm uppercase tracking-wider transition-colors ${
              activeTab === 'orders' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A3A3A3] hover:text-white'
            }`}
            data-testid="tab-orders"
          >
            <Package size={18} />
            Orders
          </button>
          <button
            onClick={() => setActiveTab('liked')}
            className={`flex items-center gap-2 pb-4 text-sm uppercase tracking-wider transition-colors ${
              activeTab === 'liked' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A3A3A3] hover:text-white'
            }`}
            data-testid="tab-liked"
          >
            <Heart size={18} />
            Liked
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="space-y-4">
                <div className="aspect-square skeleton" />
                <div className="h-4 skeleton w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {activeTab === 'purchased' && (
              <div>
                {profile?.purchased_tracks?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {profile.purchased_tracks.map(track => (
                      <PurchasedTrackCard key={track.id} track={track} tracks={profile.purchased_tracks} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-[#1A1A1A]">
                    <Music size={48} className="mx-auto text-[#737373] mb-4" />
                    <p className="text-[#A3A3A3]">No purchased tracks yet</p>
                    <a href="/music" className="text-[#D4AF37] hover:underline mt-2 inline-block">
                      Browse Music
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div>
                {profile?.orders?.length > 0 ? (
                  <div className="space-y-4">
                    {profile.orders.map(order => (
                      <div key={order.order_id} className="bg-[#1A1A1A] border border-white/10 p-6" data-testid={`order-${order.order_id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                          <div>
                            <p className="text-xs text-[#737373]">Order #{order.order_id?.slice(0, 8)}</p>
                            <p className="text-[#D4AF37] font-['Bebas_Neue'] text-xl">${order.total?.toLocaleString()}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                            order.status === 'confirmed' ? 'bg-green-900/50 text-green-400' :
                            order.status === 'shipped' ? 'bg-blue-900/50 text-blue-400' :
                            'bg-[#262626] text-[#A3A3A3]'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.name} × {item.quantity}</span>
                              <span className="text-[#A3A3A3]">{item.size} / {item.color}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-[#1A1A1A]">
                    <Package size={48} className="mx-auto text-[#737373] mb-4" />
                    <p className="text-[#A3A3A3]">No orders yet</p>
                    <a href="/store" className="text-[#D4AF37] hover:underline mt-2 inline-block">
                      Shop Now
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'liked' && (
              <div className="text-center py-20 bg-[#1A1A1A]">
                <Heart size={48} className="mx-auto text-[#737373] mb-4" />
                <p className="text-[#A3A3A3]">Liked tracks will appear here</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
